import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import {
    SYSTEM_DISPLAY_TIME_ZONE,
    formatTimestampForMerchantUi,
    type GetOrganizationLegalOverviewResponse,
    type LegalDocumentAssignmentPublic,
    type OrganizationAgreementSignatureHistoryItem,
} from '@pillar-portal/api-types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { InfiniteScrollList } from '@/components/ui/infinite-scroll-list';
import { legalSurfacingApi } from '@/lib/pillarApi';
import { runInBackground } from '@/lib/utils';
import { OrganizationAgreementReviewPage } from './OrganizationAgreementReviewPage';
import { RemoveOrganizationAssignmentButton } from './RemoveOrganizationAssignmentButton';
import {
    formatAcceptanceProgress,
    formatAssignmentInForcePeriod,
    formatAwaitingSignatureBadge,
    formatPastAssignmentEnd,
    formatSignatureExpiry,
    formatUserAgreementSource,
} from './organization-legal-card-format';
import { useActiveOrganizationSignatureSummary } from './useActiveOrganizationSignatureSummary';

/** Impersonated PRINCIPAL sessions get the read-only view: they can never sign. */
export type OrganizationLegalCardMode = 'administration' | 'principal-review' | 'read-only';

interface OrganizationLegalCardProps {
    readonly organizationUuid: string;
    readonly organizationName: string;
    readonly mode: OrganizationLegalCardMode;
}

function parseHistoryCursor(pageParam: unknown): string | undefined {
    if (pageParam === null || pageParam === undefined) {
        return undefined;
    }
    return z.string().parse(pageParam);
}

/** Shows the count of agreements awaiting signature on the card header. */
export function AwaitingSignatureBadge({ count }: { readonly count: number }) {
    const label = formatAwaitingSignatureBadge(count);
    if (label === null) {
        return null;
    }
    return (
        <Badge variant="destructive" aria-label={label}>
            {label}
        </Badge>
    );
}

/**
 * The user agreements in effect, with where each comes from and how many users
 * accepted. With allowRemoval, an agreement assigned to this Organization itself
 * can be removed; inherited ones and platform defaults cannot be removed here.
 */
export function UserAgreementsInEffect({
    overview,
    allowRemoval = false,
}: {
    readonly overview: GetOrganizationLegalOverviewResponse;
    readonly allowRemoval?: boolean;
}) {
    if (overview.user_agreements.length === 0) {
        return (
            <p className="text-sm text-gray-600">
                No user agreement applies to this Organization, so its users are not asked to accept
                one.
            </p>
        );
    }
    return (
        <ul className="space-y-2">
            {overview.user_agreements.map((agreement) => (
                <li
                    key={agreement.legal_document_assignment.legal_document_assignment_uuid}
                    className="flex flex-wrap items-baseline justify-between gap-2 rounded border p-3 text-sm"
                >
                    <div>
                        <div className="font-medium">
                            {
                                agreement.legal_document_assignment.legal_document
                                    .legal_document_title
                            }{' '}
                            (
                            {
                                agreement.legal_document_assignment.legal_document
                                    .legal_document_version
                            }
                            )
                        </div>
                        <div className="text-gray-600">
                            {formatUserAgreementSource(agreement.source)}
                        </div>
                        <div className="text-gray-600">
                            {formatAssignmentInForcePeriod(agreement.legal_document_assignment)}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {formatAcceptanceProgress(
                            agreement.accepted_user_count,
                            agreement.required_user_count
                        )}
                        {allowRemoval && agreement.source.kind === 'own' ? (
                            <RemoveOrganizationAssignmentButton
                                organizationUuid={overview.organization_uuid}
                                legalDocumentAssignmentUuid={
                                    agreement.legal_document_assignment
                                        .legal_document_assignment_uuid
                                }
                                documentLabel={`${agreement.legal_document_assignment.legal_document.legal_document_title} (${agreement.legal_document_assignment.legal_document.legal_document_version})`}
                            />
                        ) : null}
                    </div>
                </li>
            ))}
        </ul>
    );
}

export function SignatureHistoryRow({
    signature,
    now,
}: {
    readonly signature: OrganizationAgreementSignatureHistoryItem;
    readonly now: Date;
}) {
    return (
        <div className="rounded border p-3 text-sm">
            <div className="font-medium">
                {signature.legal_document_title} ({signature.legal_document_version})
            </div>
            <div>
                Signed by {signature.signer_display_name} on{' '}
                {formatTimestampForMerchantUi(signature.signed_ts, SYSTEM_DISPLAY_TIME_ZONE)}
            </div>
            <div className="text-gray-600">
                {formatSignatureExpiry(signature.expires_ts, now)}
                {signature.signer_authority_confirmed ? '' : ' · Signer authority not recorded'}
            </div>
            {signature.withdrawn_ts === null ? null : (
                <div className="text-amber-800">
                    Withdrawn{' '}
                    {formatTimestampForMerchantUi(signature.withdrawn_ts, SYSTEM_DISPLAY_TIME_ZONE)}
                    ; it no longer counts.
                </div>
            )}
        </div>
    );
}

export function PastAssignmentRow({
    assignment,
}: {
    readonly assignment: LegalDocumentAssignmentPublic;
}) {
    return (
        <div className="rounded border p-3 text-sm">
            <div className="font-medium">
                {assignment.legal_document.legal_document_title} (
                {assignment.legal_document.legal_document_version})
            </div>
            <div>
                Assigned{' '}
                {formatTimestampForMerchantUi(assignment.in_force_start, SYSTEM_DISPLAY_TIME_ZONE)}
            </div>
            <div className="text-gray-600">{formatPastAssignmentEnd(assignment)}</div>
        </div>
    );
}

function OrganizationLegalHistory({ organizationUuid }: { readonly organizationUuid: string }) {
    const now = useMemo(() => new Date(), []);
    const signaturesKey = useMemo(
        () => ['organizationAgreements', organizationUuid, 'signatureHistory'] as const,
        [organizationUuid]
    );
    const pastAssignmentsKey = useMemo(
        () => ['organizationAgreements', organizationUuid, 'pastAssignments'] as const,
        [organizationUuid]
    );
    return (
        <div className="grid gap-4 lg:grid-cols-2">
            <InfiniteScrollList<
                OrganizationAgreementSignatureHistoryItem,
                string,
                typeof signaturesKey
            >
                pageTitle="Signatures"
                cardTitle="Signatures"
                queryPlaceholder="Search signatures"
                queryKeyPrefix={signaturesKey}
                pageSize={25}
                showPageHeader={false}
                showQueryInput={false}
                fetchPage={async (params) => {
                    const response =
                        await legalSurfacingApi.searchOrganizationAgreementSignaturesPage(
                            organizationUuid,
                            { limit: params.limit, cursor: params.cursor }
                        );
                    return { items: response.signatures, pagination: response.pagination };
                }}
                parseCursorParam={parseHistoryCursor}
                stateCopy={{
                    loadingLabel: 'Loading signatures...',
                    emptyLabel: 'No agreement has been signed for this Organization.',
                    endOfListLabel: 'End of signatures.',
                    genericErrorLabel: 'Unable to load signatures.',
                }}
                renderItems={({ items }) => (
                    <div className="space-y-2">
                        {items.map((signature) => (
                            <SignatureHistoryRow
                                key={signature.organization_legal_document_acceptance_uuid}
                                signature={signature}
                                now={now}
                            />
                        ))}
                    </div>
                )}
            />
            <InfiniteScrollList<LegalDocumentAssignmentPublic, string, typeof pastAssignmentsKey>
                pageTitle="Ended assignments"
                cardTitle="Ended assignments"
                queryPlaceholder="Search ended assignments"
                queryKeyPrefix={pastAssignmentsKey}
                pageSize={25}
                showPageHeader={false}
                showQueryInput={false}
                fetchPage={async (params) => {
                    const response = await legalSurfacingApi.searchOrganizationPastAssignmentsPage(
                        organizationUuid,
                        { limit: params.limit, cursor: params.cursor }
                    );
                    return {
                        items: response.legal_document_assignments,
                        pagination: response.pagination,
                    };
                }}
                parseCursorParam={parseHistoryCursor}
                stateCopy={{
                    loadingLabel: 'Loading ended assignments...',
                    emptyLabel: 'No assignment to this Organization has ended.',
                    endOfListLabel: 'End of ended assignments.',
                    genericErrorLabel: 'Unable to load ended assignments.',
                }}
                renderItems={({ items }) => (
                    <div className="space-y-2">
                        {items.map((assignment) => (
                            <PastAssignmentRow
                                key={assignment.legal_document_assignment_uuid}
                                assignment={assignment}
                            />
                        ))}
                    </div>
                )}
            />
        </div>
    );
}

function AdministrationLegalSections({
    organizationUuid,
    organizationName,
}: {
    readonly organizationUuid: string;
    readonly organizationName: string;
}) {
    const overviewQuery = useQuery<GetOrganizationLegalOverviewResponse>({
        queryKey: ['organizationAgreements', organizationUuid, 'legalOverview'],
        queryFn: async () => await legalSurfacingApi.getOrganizationLegalOverview(organizationUuid),
    });
    return (
        <>
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
                <CardTitle>Legal</CardTitle>
                <AwaitingSignatureBadge count={overviewQuery.data?.awaiting_signature_count ?? 0} />
            </CardHeader>
            <CardContent className="space-y-6">
                <section className="space-y-2">
                    <h3 className="font-semibold">User agreements</h3>
                    {overviewQuery.isLoading ? (
                        <p className="text-sm">Loading user agreements...</p>
                    ) : overviewQuery.isError || overviewQuery.data === undefined ? (
                        <div role="alert" className="space-y-2 text-sm text-red-700">
                            <p>Unable to load the user agreements in effect.</p>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() =>
                                    runInBackground(overviewQuery.refetch(), () => undefined)
                                }
                            >
                                Retry
                            </Button>
                        </div>
                    ) : (
                        <UserAgreementsInEffect overview={overviewQuery.data} allowRemoval={true} />
                    )}
                </section>
                <section className="space-y-2">
                    <h3 className="font-semibold">Organization agreements</h3>
                    <OrganizationAgreementReviewPage
                        organizationUuid={organizationUuid}
                        organizationName={organizationName}
                        allowAcceptance={false}
                        allowAssignment={true}
                    />
                </section>
                <section className="space-y-2">
                    <h3 className="font-semibold">History</h3>
                    <OrganizationLegalHistory organizationUuid={organizationUuid} />
                </section>
            </CardContent>
        </>
    );
}

function PrincipalLegalSections({
    organizationUuid,
    organizationName,
    allowAcceptance,
}: {
    readonly organizationUuid: string;
    readonly organizationName: string;
    readonly allowAcceptance: boolean;
}) {
    const { summary } = useActiveOrganizationSignatureSummary();
    const awaitingSignatureCount =
        summary?.summary_kind === 'organization' && summary.organization_uuid === organizationUuid
            ? summary.awaiting_signature_count
            : 0;
    return (
        <>
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
                <CardTitle>Legal</CardTitle>
                <AwaitingSignatureBadge count={awaitingSignatureCount} />
            </CardHeader>
            <CardContent>
                <OrganizationAgreementReviewPage
                    organizationUuid={organizationUuid}
                    organizationName={organizationName}
                    allowAcceptance={allowAcceptance}
                />
            </CardContent>
        </>
    );
}

/**
 * The Organization's Legal card. Internal administrators see where each user
 * agreement comes from, acceptance progress, the Organization agreements and
 * their full history. A PRINCIPAL reviews and signs its Organization's
 * agreements here.
 */
export function OrganizationLegalCard({
    organizationUuid,
    organizationName,
    mode,
}: OrganizationLegalCardProps) {
    return (
        <Card id="organization-legal-card">
            {mode === 'administration' ? (
                <AdministrationLegalSections
                    organizationUuid={organizationUuid}
                    organizationName={organizationName}
                />
            ) : (
                <PrincipalLegalSections
                    organizationUuid={organizationUuid}
                    organizationName={organizationName}
                    allowAcceptance={mode === 'principal-review'}
                />
            )}
        </Card>
    );
}
